import React, { useEffect, useRef, useState } from "react";
import { Cropper } from "react-cropper";
import Webcam from "react-webcam";
import "cropperjs/dist/cropper.min.css";
import imageCompression from "browser-image-compression";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  FormHelperText,
  Paper,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import { useForm } from "react-hook-form";
import ValidateFile from "../../../../utils/ValidateFile";
import { toast } from "react-toastify";
import PropTypes from 'prop-types';

const PassportPhotoInput = ({ handleFileForDocs, prefilledUrl }) => {
  const { register, setValue, formState: { errors } } = useForm();
  const fileInputRef = useRef(null);
  const cropperRef = useRef(null);
  const webcamRef = useRef(null);

  const MIN_FILE_SIZE = 10 * 1024; // 10 KB
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MIN_WIDTH = 100;
  const MIN_HEIGHT = 100;

  const [photo, setPhoto] = useState("");
  const [croppedImage, setCroppedImage] = useState(null);
  const [imageSizeError, setImageSizeError] = useState("");
  const [isCropping, setIsCropping] = useState(false);
  const [zoomLock, setZoomLock] = useState(false);
  const [borderRadius, setBorderRadius] = useState("50%");
  const [useCamera, setUseCamera] = useState(false);

  useEffect(() => {
    if (prefilledUrl) {
      setCroppedImage(prefilledUrl);
      handleFileForDocs?.({ file: null, url: prefilledUrl }, "photo");
    }
  }, [prefilledUrl]);

  const triggerFileInput = () => fileInputRef.current?.click();

  const handleImageSelect = (e) => {
    const file = e.target.files[0];

    // Allow selecting the same file again
    e.target.value = "";

    if (!file) {
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    // 1. File type validation
    const validate = ValidateFile(file, allowedTypes);

    if (validate !== true) {
      setImageSizeError(validate);
      toast.error(validate, { className: "toast-error-custom" });
      return;
    }

    // 2. Maximum file size validation - 10 MB
    if (file.size > MAX_FILE_SIZE) {
      const message = "Image size should not exceed 10 MB.";
      setImageSizeError(message);
      toast.error(message, { className: "toast-error-custom" });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      // 3. Minimum resolution validation - 100x100
      if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
        const message = "Image resolution must be at least 100 x 100 px.";

        URL.revokeObjectURL(objectUrl);

        setImageSizeError(message);
        toast.error(message, { className: "toast-error-custom" });
        return;
      }

      // 4. Reset previous state
      setImageSizeError("");
      setCroppedImage(null);
      setPhoto(objectUrl);
      setIsCropping(true);
      setZoomLock(false);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      const message = "Invalid image file.";
      setImageSizeError(message);
      toast.error(message, { className: "toast-error-custom" });
    };

    img.src = objectUrl;
  };

  const captureFromCamera = () => {
    const imageSrc = webcamRef.current.getScreenshot({ width: 600, height: 600 });
    if (!imageSrc) return;

    fetch(imageSrc)
      .then((res) => res.blob())
      .then(async (blob) => {
        let finalFile = new File([blob], `camera-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        let sizeKB = finalFile.size / 1024;

        if (sizeKB > 3072) {
          try {
            const compressed = await imageCompression(finalFile, {
              maxSizeMB: 10,
              //maxWidthOrHeight: 600,
              initialQuality: 0.9,
              useWebWorker: true,
            });
            finalFile = compressed;
          } catch {
            setImageSizeError("Camera image should be less than 3MB.");
            return;
          }
        }

        const url = URL.createObjectURL(finalFile);
        setValue?.("photo", finalFile);
        setCroppedImage(url);
        handleFileForDocs?.({ file: finalFile, url }, "photo");
        setPhoto("");
        setIsCropping(false);
        setUseCamera(false);
        setImageSizeError("");
      });
  };

  const cropImage = async () => {
    if (!cropperRef.current?.cropper) return;
    const cropper = cropperRef.current.cropper;

    const croppedCanvas = cropper.getCroppedCanvas({
      // width: 600,
      // height: 600,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    if (!croppedCanvas) {
      setImageSizeError("Image cropping failed. Try again.");
      return;
    }

    croppedCanvas.toBlob(async (blob) => {
      if (!blob) return;
      let finalFile = new File([blob], `passport-photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      if (finalFile.size > MAX_FILE_SIZE) {
        try {
          const compressed = await imageCompression(finalFile, {
            maxSizeMB: 10,
            initialQuality: 0.9,
            useWebWorker: true,
          });

          finalFile = compressed;
        } catch {
          setImageSizeError("Compression failed. Try again.");
          return;
        }
      }

      // Final maximum size validation
      if (finalFile.size > MAX_FILE_SIZE) {
        setImageSizeError("Final image should not exceed 10 MB.");
        return;
      }

      // Final minimum size validation
      if (finalFile.size < MIN_FILE_SIZE) {
        setImageSizeError("Final image should be at least 10 KB.");
        return;
      }

      const url = URL.createObjectURL(finalFile);
      setValue?.("photo", finalFile);
      setCroppedImage(url);
      handleFileForDocs?.({ file: finalFile, url }, "photo");
      setIsCropping(false);
      setImageSizeError("");
    }, "image/jpeg", 0.95);
  };

  const removePhoto = () => {
    setPhoto("");
    setCroppedImage(null);
    setValue?.("photo", null);
    handleFileForDocs?.(null, "photo");
    setZoomLock(false);
    setBorderRadius("50%");
    setImageSizeError("");
    setUseCamera(false);
  };

  useEffect(() => {
    register?.("photo",);
  }, [register]);

  const renderPhotoContent = () => {
    if (croppedImage) {
      return (
        <Box
          component="img"
          src={croppedImage}
          alt="Preview"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      );
    }

    if (useCamera) {
      return (
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
          screenshotWidth={600}
          videoConstraints={{ width: 600, height: 600, facingMode: "user" }}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      );
    }

    if (photo) {
      return (
        <Cropper
          src={photo}
          style={{
            height: "100%",
            width: "100%",
            borderRadius,
          }}
          aspectRatio={1}
          guides={false}
          ref={cropperRef}
          viewMode={0}
          background={false}
          responsive
          autoCropArea={1}
          dragMode="move"
          movable
          zoomable={!zoomLock}
          cropBoxMovable
          cropBoxResizable
          ready={() => {
            const cropper = cropperRef.current?.cropper;

            if (!cropper) return;

            cropper.reset();

            const containerData = cropper.getContainerData();
            const canvasData = cropper.getCanvasData();

            cropper.setCanvasData({
              left: (containerData.width - canvasData.width) / 2,
              top: (containerData.height - canvasData.height) / 2,
            });
          }}
        />
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <IconButton color="primary" component="span">
          <PhotoCameraIcon sx={{ fontSize: 40 }} />
        </IconButton>
        <Typography variant="body2" color="textSecondary">
          Upload Photo
        </Typography>
      </Box>
    );
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          width: 180,
          height: 180,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: errors?.photo ? "2px solid red" : "1px dashed grey",
          borderRadius: "50%",
          overflow: "hidden",
          position: "relative",
          cursor: !photo && !useCamera ? "pointer" : "default",
        }}
        onClick={!photo && !useCamera ? triggerFileInput : undefined}
      >
        {renderPhotoContent()}

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageSelect}
        />
      </Paper>

      {errors?.photo && <FormHelperText error>Passport photo is required.</FormHelperText>}
      {imageSizeError && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <FormHelperText error>{imageSizeError}</FormHelperText>
          <Button variant="outlined" size="small" onClick={triggerFileInput}>
            Re-upload
          </Button>
        </Box>
      )}

      {isCropping && (
        <Button variant="contained" color="primary" onClick={cropImage}>
          Crop & Save
        </Button>
      )}

      {useCamera && !croppedImage && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" onClick={captureFromCamera}>
            Capture
          </Button>
          <Button variant="outlined" onClick={() => setUseCamera(false)}>
            Cancel
          </Button>
        </Box>
      )}

      {/* {!useCamera && !photo && !croppedImage && (
        <Button
          variant="outlined"
          startIcon={<PhotoCameraIcon />}
          onClick={() => setUseCamera(true)}
        >
          Open Camera
        </Button>
      )} */}

      {croppedImage && (
        <IconButton color="error" onClick={removePhoto}>
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );
};

PassportPhotoInput.propTypes = {
  handleFileForDocs: PropTypes.func.isRequired,
  prefilledUrl: PropTypes.string
};
export default PassportPhotoInput;
